import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mic, Square, Save, FileText, AlertTriangle, EyeOff, Play } from 'lucide-react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { sessionService } from '../services/SessionService';
import { storage } from '../config/firebase';
import { Session } from '../models/Session';
import { useVoiceCommands } from '../hooks/useVoiceCommands';
import {
  createDictationEngine,
  resolveDictationProvider,
  DictationEngine,
  DictationStatus,
} from '../services/dictation';

// Dictation backend (Web Speech by default; Deepgram nova-2 streaming when
// REACT_APP_DICTATION_PROVIDER=deepgram — the iOS-WKWebView-safe path).
const DICTATION_PROVIDER = resolveDictationProvider();

// Play one of the pre-recorded VERA confirmation clips (best-effort).
const playVeraAudio = (file: string) => {
  try {
    const audio = new Audio(`${process.env.PUBLIC_URL || ''}/audio/vera/${file}`);
    audio.play().catch(() => {
      /* autoplay/permission — non-fatal */
    });
  } catch {
    /* noop */
  }
};

// MediaRecorder mime for the raw-audio archive (source of truth). Prefer
// webm/opus (Chrome/Android); fall back to mp4/aac so iOS Safari/WKWebView also
// captures the raw recording (it does not support webm).
function getSupportedAudioMime(): string | null {
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) return null;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* noop */
    }
  }
  return null;
}

// Storage object extension for a MediaRecorder mime.
function extForMime(mime: string): string {
  return mime.startsWith('audio/mp4') ? 'm4a' : 'webm';
}

const fmtClock = (secs: number) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const card: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-md)',
  border: '1px solid var(--color-border)',
  padding: 'var(--spacing-6)',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-3) var(--spacing-5)',
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 'var(--font-semibold)',
  cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'transparent',
  color: 'var(--color-primary)',
  border: '1px solid var(--color-primary)',
  borderRadius: 'var(--radius-md)',
  fontWeight: 'var(--font-medium)',
  cursor: 'pointer',
};

export const DictateNotes: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [recording, setRecording] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [privacyMode, setPrivacyMode] = useState(false);

  // Dictation engine (provider chosen by REACT_APP_DICTATION_PROVIDER). Built
  // once; its callbacks drive the same transcript/interim UX as before.
  const engineRef = useRef<DictationEngine | null>(null);
  const [dictationStatus, setDictationStatus] = useState<DictationStatus>('idle');
  if (engineRef.current === null && DICTATION_PROVIDER !== 'off') {
    engineRef.current = createDictationEngine({
      onFinal: (text) =>
        setTranscript((prev) => (prev ? `${prev} ${text}`.trim() : text.trim())),
      onInterim: (text) => setInterim(text),
      onError: (msg) => setError(msg),
      onStatus: (s) => setDictationStatus(s),
    });
  }
  const supported = DICTATION_PROVIDER !== 'off' && (engineRef.current?.isSupported() ?? false);

  // Raw audio capture (MediaRecorder). Silent no-op if unsupported.
  const audioMime = getSupportedAudioMime();
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [audioActive, setAudioActive] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioBlobRef = useRef<Blob | null>(null);
  const durationRef = useRef<number | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!user || !sessionId) return;
      try {
        const s = await sessionService.getSession(user.companyId, sessionId);
        if (!active) return;
        if (!s) {
          setNotFound(true);
          return;
        }
        setSession(s);
        setTranscript(s.dictationTranscript || '');
      } catch (e) {
        console.error('DictateNotes load error:', e);
        setError('Could not load this session.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
      try {
        void engineRef.current?.stop();
      } catch {
        /* noop */
      }
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch {
        /* noop */
      }
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      if (durationRef.current) clearInterval(durationRef.current);
    };
  }, [user, sessionId]);

  const stop = () => {
    setRecording(false);
    setPrivacyMode(false);
    void engineRef.current?.stop();
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch {
      /* noop */
    }
    mediaRecorderRef.current = null;
    if (durationRef.current) {
      clearInterval(durationRef.current);
      durationRef.current = null;
    }
  };

  const start = async () => {
    setError(null);
    setSaved(false);

    // Open ONE mic stream: the raw-audio recorder (source of truth) and the
    // streaming transcription engine share it (on the Deepgram path).
    let stream: MediaStream | null = null;
    if (audioMime) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;
        audioChunksRef.current = [];
        audioBlobRef.current = null;
        const mr = new MediaRecorder(stream, { mimeType: audioMime });
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
        };
        mr.onstop = () => {
          const blob = new Blob(audioChunksRef.current, { type: audioMime });
          audioBlobRef.current = blob.size > 0 ? blob : null;
          mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
          mediaStreamRef.current = null;
          setAudioActive(false);
        };
        mr.start();
        mediaRecorderRef.current = mr;
        setAudioActive(true);
        setRecordSeconds(0);
        if (durationRef.current) clearInterval(durationRef.current);
        durationRef.current = window.setInterval(() => setRecordSeconds((s) => s + 1), 1000);
      } catch {
        /* silent: transcript-only mode (raw capture unavailable) */
      }
    }

    // Speech-to-text — shares the mic stream when we have one (Deepgram);
    // Web Speech ignores it and manages its own mic.
    try {
      await engineRef.current?.start({
        stream: stream ?? undefined,
        // Bias transcription toward this client's name (+ the ABA glossary).
        keyterms: session?.clientName ? [session.clientName] : undefined,
      });
    } catch (e: any) {
      const msg = String(e?.message || e || '');
      if (/denied|permission|notallowed|not allowed/i.test(msg)) {
        setError('Microphone access is needed for dictation.');
      } else if (msg) {
        setError(msg);
      }
    }

    setRecording(true);
  };

  // Privacy Mode — shared by the voice command ("Hey VERA") and the on-screen
  // button. Pauses capture so nothing is recorded/streamed.
  const enterPrivacyMode = () => {
    if (privacyMode) return;
    setPrivacyMode(true);
    // Pause transcription so speech isn't captured while private.
    engineRef.current?.pause();
    // Pause raw audio capture.
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.pause();
      }
    } catch {
      /* noop */
    }
    // Disable any video tracks (if a WebRTC stream is active).
    try {
      mediaStreamRef.current?.getVideoTracks().forEach((t) => (t.enabled = false));
    } catch {
      /* noop */
    }
    playVeraAudio('vera-privacy-mode.mp3');
    if (user && sessionId) {
      sessionService
        .startPrivacyPause(user.companyId, sessionId)
        .catch((e) => console.error('Privacy pause log failed:', e));
    }
  };

  const exitPrivacyMode = () => {
    if (!privacyMode) return;
    setPrivacyMode(false);
    // Resume raw audio capture.
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
      }
    } catch {
      /* noop */
    }
    // Re-enable video tracks.
    try {
      mediaStreamRef.current?.getVideoTracks().forEach((t) => (t.enabled = true));
    } catch {
      /* noop */
    }
    // Resume transcription if the session is still recording.
    if (recording) engineRef.current?.resume();
    playVeraAudio('vera-resuming.mp3');
    if (user && sessionId) {
      sessionService
        .endPrivacyPause(user.companyId, sessionId)
        .catch((e) => console.error('Privacy resume log failed:', e));
    }
  };

  // "Hey VERA" wake word runs ONLY while actively recording a session. Each
  // detection toggles Privacy Mode (pause ↔ resume).
  const voice = useVoiceCommands({
    isSessionActive: recording,
    onWakeWord: () => (privacyMode ? exitPrivacyMode() : enterPrivacyMode()),
  });

  const save = async (): Promise<boolean> => {
    if (!user || !sessionId) return false;
    setSaving(true);
    setError(null);
    try {
      // Raw dictation audio is the source-of-truth record — save it unedited,
      // additive to the transcript, never overwriting the original.
      let audioUrl: string | undefined;
      const blob = audioBlobRef.current;
      if (blob && blob.size > 0) {
        const mime = audioMime || 'audio/webm';
        const ext = extForMime(mime);
        const path = `companies/${user.companyId}/sessions/${sessionId}/dictation-audio.${ext}`;
        const sref = storageRef(storage, path);
        await uploadBytes(sref, blob, { contentType: mime });
        audioUrl = await getDownloadURL(sref);
      }
      await sessionService.saveDictation(user.companyId, sessionId, transcript.trim(), audioUrl);
      setSaved(true);
      return true;
    } catch (e) {
      console.error('DictateNotes save error:', e);
      setError('Could not save the transcript. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const continueToNotes = async () => {
    if (recording) stop();
    const ok = await save();
    if (ok) navigate(`/my-sessions/${sessionId}/notes`);
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Privacy Mode banner — visual confirmation alongside the audio cue */}
      {privacyMode && (
        <div
          role="status"
          aria-live="assertive"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 300,
            backgroundColor: '#7a1020',
            color: '#ffffff',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <EyeOff size={18} />
            Privacy Mode — recording and video are paused
          </span>
          <button
            type="button"
            onClick={exitPrivacyMode}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              backgroundColor: '#ffffff',
              color: '#7a1020',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <Play size={14} />
            Resume
          </button>
        </div>
      )}

      <button
        onClick={() => navigate('/my-sessions')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          background: 'none',
          border: 'none',
          color: 'var(--color-primary)',
          cursor: 'pointer',
          marginBottom: 'var(--spacing-5)',
          fontWeight: 'var(--font-medium)',
        }}
      >
        <ArrowLeft size={18} />
        Back to My Sessions
      </button>

      {loading ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>Loading…</div>
      ) : notFound ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>Session not found.</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
            <Mic size={22} style={{ color: 'var(--color-primary)' }} />
            <div>
              <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-primary)' }}>
                Dictate Notes
              </h1>
              {session && (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{session.clientName}</div>
              )}
            </div>
          </div>

          {!supported && (
            <div
              style={{
                ...card,
                marginBottom: 'var(--spacing-4)',
                backgroundColor: 'rgba(196,154,42,0.12)',
                borderColor: 'var(--color-accent)',
                display: 'flex',
                gap: 'var(--spacing-2)',
                alignItems: 'flex-start',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <AlertTriangle size={16} style={{ color: 'var(--color-accent-dark)', flexShrink: 0, marginTop: 2 }} />
              {DICTATION_PROVIDER === 'off'
                ? 'Voice dictation is turned off in this build. You can type your notes below.'
                : "Voice input isn't supported in this browser (try Chrome or Edge). You can still type your notes below."}
            </div>
          )}

          {error && (
            <div style={{ ...card, marginBottom: 'var(--spacing-4)', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
              {error}
            </div>
          )}

          <div style={{ ...card, display: 'grid', gap: 'var(--spacing-4)' }}>
            {/* Record control */}
            {supported && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => (recording ? stop() : start())}
                  style={{
                    ...btnPrimary,
                    backgroundColor: recording ? 'var(--color-error)' : 'var(--color-primary)',
                  }}
                >
                  {recording ? <Square size={16} /> : <Mic size={16} />}
                  {recording ? 'Stop' : 'Start Recording'}
                </button>
                {/* Privacy Pause — fallback for the "Hey VERA" voice command */}
                {recording && !privacyMode && (
                  <button type="button" onClick={enterPrivacyMode} style={btnGhost}>
                    <EyeOff size={16} />
                    Privacy Pause
                  </button>
                )}
                {recording && privacyMode && (
                  <button
                    type="button"
                    onClick={exitPrivacyMode}
                    style={{ ...btnPrimary, backgroundColor: 'var(--color-success)' }}
                  >
                    <Play size={16} />
                    Resume
                  </button>
                )}
                {recording && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-error)',
                      fontWeight: 'var(--font-medium)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-error)',
                        display: 'inline-block',
                        animation: 'vera-rec-pulse 1.2s ease-in-out infinite',
                      }}
                    />
                    {audioActive ? `Recording ${fmtClock(recordSeconds)}` : 'Listening…'}
                  </span>
                )}
                {recording && dictationStatus === 'reconnecting' && (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-accent-dark)', fontWeight: 'var(--font-medium)' }}>
                    Reconnecting…
                  </span>
                )}
                <style>{`
                  @keyframes vera-rec-pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.35; }
                  }
                `}</style>
              </div>
            )}

            {/* Voice command status (graceful when the mic is denied) */}
            {recording && (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                {voice.status === 'loading' && 'Starting “Hey VERA”…'}
                {voice.status === 'listening' &&
                  'Voice on — say “Hey VERA” to pause or resume.'}
                {(voice.status === 'denied' || voice.status === 'error') &&
                  `${voice.error || 'Voice commands unavailable.'} You can still use the Privacy Pause button.`}
              </div>
            )}

            {/* Transcript (editable) */}
            <div>
              <label
                htmlFor="transcript"
                style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                Transcript
              </label>
              <textarea
                id="transcript"
                rows={10}
                value={interim ? `${transcript}${transcript ? ' ' : ''}${interim}` : transcript}
                onChange={(e) => {
                  setTranscript(e.target.value);
                  setInterim('');
                  setSaved(false);
                }}
                placeholder="Your dictated notes will appear here. You can also type or edit directly."
                style={{
                  width: '100%',
                  padding: 'var(--spacing-3)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'var(--font-family-base)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-primary)',
                  backgroundColor: 'var(--color-white)',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'center' }}>
              <button style={{ ...btnGhost, opacity: saving ? 0.6 : 1 }} onClick={save} disabled={saving}>
                <Save size={16} />
                {saving ? 'Saving…' : 'Save Transcript'}
              </button>
              <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={continueToNotes} disabled={saving}>
                <FileText size={16} />
                Continue to Write Notes
              </button>
              {saved && (
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-success)', fontWeight: 'var(--font-medium)' }}>
                  Saved
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DictateNotes;
